import React, { useContext, useEffect } from 'react';
import { CampaignChallenge, useCampaignChallengesQuery, useUpdateCampaignChallengeMutation } from '../../generated/graphql';
import FieldTable, { FieldTableColumnProps } from '../../components/field-table';
import { isoDateToMMMWithTime } from '../../utils/index';
import { useUserContext } from '../../contexts/user-context';
import { Accordion, AccordionDetails, AccordionSummary, Alert, AlertTitle, Button, Tab, Tooltip, Typography } from '@mui/material';
import { Circle } from '@mui/icons-material';
import { Paper } from '@mui/material';
import { useCallback } from 'react';
import { Cancel } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import WaitSpinner from '../../components/wait-spinner';
import { Tabs } from '@mui/material';
import { format, subDays } from 'date-fns';
import { ToolbarContext } from '../../contexts/toolbar-context';

const columns: FieldTableColumnProps<CampaignChallenge>[] = [
    { header: '', dataType: 'expander', className: 'fred' },
    {
        header: '', accessor: 'status',
        width: 'minmax(1em,max-content)',
        dataType: 'text',
        readOnly:true,
        options: {
            renderViewer: ({ field }) => {
                switch (field.value) {
                    case 'OPEN':
                        return <Tooltip title="The request has not been actioned"><Circle color="info" /></Tooltip>;
                    case 'REJECT':
                        return <Tooltip title="The release request was rejected"><Circle color="error" /></Tooltip>;
                    case 'RELEASE':
                        return <Tooltip title="The pole was released to this campaign"><Circle color="success" /></Tooltip>;
                    case 'CANCEL':
                        return <Tooltip title="Cancelled (this challenge is no longer required)"><Circle color="secondary" /></Tooltip>;
                }
            },
        }
    },
    {
        header: 'Contact', accessor: 'targetCampaign.ourContact.name',
        readOnly:true,
        dataType: 'text'
    },
    {
        header: 'Site', accessor: 'panel.site.name',
        readOnly:true,
        dataType: 'text',
        options: {
            getValue: ({ row }) => {
                return row?.panel?.site?.name + ': ' + row.panel?.panel;
            }
        }
    },
    {
        header: 'Region',
        accessor: 'panel.site.region',
        readOnly:true,
        dataType: 'text',
        options: {
            getValue: ({ row }) => {
                return row?.panel?.site?.region?.regionType?.name + ' / ' + row?.panel?.site?.region?.name;
            }
        }
    },
    {
        header: 'Campaign', accessor: 'targetCampaign.job_reference',
        readOnly:true,
        dataType: 'link',
        options: {
            getLink({ row }) {
                console.log('row is', row);
                return '/campaigns/' + row?.targetCampaign?.job_reference;
            }
        }
    }
];


export const CampaignChallenges = () => {
    const { enqueueSnackbar } = useSnackbar();
    const user = useUserContext();
    const challenges = useCampaignChallengesQuery({ variables: { user_id: user?.user?.id ?? '0', direction: 'outbound', markViewed: true }, fetchPolicy: 'network-only' });
    const [hideChallenges] = useUpdateCampaignChallengeMutation();
    useEffect(() => {
        if (challenges && !challenges.loading) {
            challenges.refetch();
        }
    }, [user.activeChallengeCountInbound, challenges]);
    const doHideChallenges = useCallback(async ({ id, status, currentStatus }) => {
        if (currentStatus && (currentStatus != 'CANCEL' && status == 'CANCEL')) {
            if (!confirm('This will cancel any booking challenges that have not been completed')) {
                return;
            }
        }
        enqueueSnackbar('Cancelling challenge');
        const args:any = { id: id, hide: true };
        if (status) { args.status = status; }
        console.log('args are', args);
        await hideChallenges({ variables: args });
        enqueueSnackbar('Challenge hidden', { variant: 'success' });
    }, []);
    const activeData:JSX.Element[] = [];
    const completedData:JSX.Element[] = [];
    let hasData = false;
    const toolbarContext = useContext(ToolbarContext);
    useEffect(() => {
        toolbarContext.setToolbar(
          {
            primary: [],
            title: 'My Challenges',
            secondary: []
          });
      }, []);
    const doDelete = async (challengeId,pbId)=> {
        enqueueSnackbar('Cancelling panel booking challenge');
        const args = { id: challengeId,cancelPanelBookings:[pbId] };
        try {
            await hideChallenges({ variables: args });
            challenges.refetch();
        } catch(e) {
            enqueueSnackbar(e&&e.message?e.message:'Unable to cancel panel booking challenge',{variant:'error'});
            return;
        }
        enqueueSnackbar('Panel booking challenge cancelled',{variant:'success'});
    }

    const nowMinusOne = format(subDays(new Date(),1),'yyyy-MM-dd hh:mm:ss');
    challenges.data?.campaignChallenges?.forEach((challenge)=>{
        hasData = true;
        let pushTo = 'COMPLETED';
        if(challenge?.status=='LIVE') {
            challenge.bookingChallenges?.forEach((v)=>{
                if(v?.status=='OPEN') {
                    pushTo = 'LIVE';
                }
            });
        }
        const value =  (<Accordion key={challenge?.id} defaultExpanded={true}>
            <AccordionSummary sx={(theme)=>({backgroundColor:((pushTo=='LIVE' && nowMinusOne>(challenge?.created_at??0))?theme.palette.error.dark:theme.palette.primary.dark),
            color:theme.palette.primary.contrastText})}>
                <Typography style={{position:'relative',width:'100%'}}>{challenge?.campaign?.job_reference} <i> {challenge?.campaign?.name}</i>
                <span style={{ position: 'absolute', right: '80px', top: 0, zIndex: 2, backgroundColor: 'inherit', height: '100%', display: 'flex',alignItems:'center' }}>{isoDateToMMMWithTime(challenge.created_at,false)}</span></Typography>
            </AccordionSummary>
            <AccordionDetails>
                <FieldTable values={challenge?.bookingChallenges} columns={columns} hasDelete={true}
                doDelete={(id)=>{doDelete(challenge?.id,id)}}
                    loading={challenges.loading} footer={<>
                        {pushTo == 'LIVE' &&
                            <Button variant="contained" color="warning"
                                onClick={() => {
                                    doHideChallenges({ id: challenge?.id, currentStatus: challenge?.status, status: 'CANCEL' });
                                }}
                            ><Cancel /> Cancel all outstanding booking challenges</Button>
                        }
                    </>}>
                    {({ row }) => (
                        <Paper sx={{ padding: '5px' }}>{row.message != '' ? row.message : 'No details'}</Paper>)
                    }</FieldTable>
            </AccordionDetails>
        </Accordion>)

        if(pushTo=='LIVE') {
            activeData.push(value);
        } else {
            completedData.push(value);
        }
    });

    const [tab, setTab] = React.useState('LIVE');
    const changeTab = (evt, value) => {
        setTab(value);
    };
    return (<div className="campaign-challenges is-scrollable">{!challenges.data && <WaitSpinner variant="panel" />}
        {challenges.data != null && hasData &&
            <>
                <Tabs value={tab} onChange={changeTab}>
                    <Tab value="LIVE" label="Live challenges" />
                    <Tab value="COMPLETED" label="Completed challenges" />
                </Tabs>
                {tab == 'LIVE' && activeData}
                {tab == "COMPLETED" && completedData}
            </>
        }
        {challenges.data!=null && !hasData?<Alert severity="success"><AlertTitle>You have no outstanding requests</AlertTitle></Alert>:[]}
        
        </div>);
}